import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import {
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

interface PromoCode {
  _id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  isActive: boolean;
  usageCount: number;
  maxUsage?: number;
  expiresAt?: string;
  createdAt: string;
  providerId?: string;
  partnerId?: string;
  partnerName?: string;
  partnerEmail?: string;
}

const PromoCodeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState<PromoCode | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Form data
  const [formData, setFormData] = useState({
    isActive: false,
    expiresAt: ''
  });

  useEffect(() => {
    if (id) {
      loadPromoCode();
    }
  }, [id]);

  const loadPromoCode = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl(`/api/promocodes/${id}`), {
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération du code promo');
      }

      const promoCodeData = await response.json();
      setPromoCode(promoCodeData);
      setFormData({
        isActive: promoCodeData.isActive,
        expiresAt: promoCodeData.expiresAt ? promoCodeData.expiresAt.split('T')[0] : ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('@TikodAdmin:token');
      const updateData: any = {
        isActive: formData.isActive
      };

      if (formData.expiresAt) {
        updateData.expiresAt = formData.expiresAt;
      }

      const response = await fetch(buildApiUrl(`/api/promocodes/${id}`), {
        method: 'PUT',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour du code promo');
      }

      const updatedPromoCode = await response.json();
      setPromoCode(updatedPromoCode);
      setSuccess('Code promo mis à jour avec succès');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getDiscountDisplay = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `${value}€`;
  };

  const getStatusColor = (isActive: boolean, expiresAt?: string) => {
    if (!isActive) return 'error';
    if (expiresAt && new Date(expiresAt) < new Date()) return 'warning';
    return 'success';
  };

  const getStatusLabel = (isActive: boolean, expiresAt?: string) => {
    if (!isActive) return 'Inactif';
    if (expiresAt && new Date(expiresAt) < new Date()) return 'Expiré';
    return 'Actif';
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!promoCode) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
        <Alert severity="error">
          Code promo non trouvé
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
      <Box 
        display="flex" 
        alignItems="center" 
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/promocodes')}
          sx={{ mr: { sm: 2 }, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
          size={isMobile ? 'small' : 'medium'}
        >
          Retour
        </Button>
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Détails du code promo
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            Informations générales
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 2 }, 
            mb: 2, 
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Chip 
              label={`ID: ${promoCode._id}`} 
              variant="outlined" 
              size={isMobile ? 'small' : 'medium'}
            />
            <Chip 
              label={`Créé le: ${new Date(promoCode.createdAt).toLocaleDateString('fr-FR')}`} 
              variant="outlined" 
              size={isMobile ? 'small' : 'medium'}
            />
            <Chip
              label={getStatusLabel(promoCode.isActive, promoCode.expiresAt)}
              size={isMobile ? 'small' : 'medium'}
              color={getStatusColor(promoCode.isActive, promoCode.expiresAt) as any}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Informations du code promo */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            Code promo: <strong>{promoCode.code}</strong>
          </Typography>
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: { xs: 2, sm: 3 },
            mt: 2
          }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Type de réduction
              </Typography>
              <Chip 
                label={promoCode.discountType === 'percentage' ? 'Pourcentage' : 'Montant fixe'} 
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Valeur de la réduction
              </Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                {getDiscountDisplay(promoCode.discountType, promoCode.discountValue)}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Utilisations
              </Typography>
              <Typography variant="body1">
                {promoCode.usageCount}
                {promoCode.maxUsage && ` / ${promoCode.maxUsage}`}
              </Typography>
            </Box>
            
            {promoCode.partnerName && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Partenaire
                </Typography>
                <Typography variant="body1">
                  {promoCode.partnerName}
                </Typography>
                {promoCode.partnerEmail && (
                  <Typography variant="body2" color="text.secondary">
                    {promoCode.partnerEmail}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Formulaire de modification */}
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" gutterBottom sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            Paramètres modifiables
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                size={isMobile ? 'small' : 'medium'}
              />
            }
            label="Code promo actif"
          />

          <TextField
            label="Date d'expiration"
            type="date"
            value={formData.expiresAt}
            onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Laissez vide pour aucune expiration"
            size={isMobile ? 'small' : 'medium'}
          />

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PromoCodeDetails; 